import { Dispatch, FC, SetStateAction } from "react";
import { FormType } from "../../common";
import { DairyV2Input } from "../../../../models/Intervention/DairyV2Intervention";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { useFormContext } from "react-hook-form";
import { unitLong } from "../../../../utils/unit-utils";
import { Spacer } from "../../../helpers/Spacer";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import RowTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";
import { DsmGrid } from "@dsm-dcs/design-system-react";
import { isFieldFilled } from "../CommonDataParts/InterventionGenericInfoDialog";
import EditModeButtons from "../../../helpers/EditModeButtons";

interface DairyV2InputStockCowsComponentProps {
  fieldItemPrefix: string;
  formType: FormType;
  defaultStageData: DairyV2Input;
  isStockEdit: boolean;
  setIsStockEdit: Dispatch<SetStateAction<boolean>>;
  resetEdit: () => void;
};

const DairyV2InputStockCowsComponent: FC<DairyV2InputStockCowsComponentProps> = ({
  fieldItemPrefix,
  formType,
  defaultStageData,
  isStockEdit,
  setIsStockEdit,
  resetEdit,
}) => {
  const intl = useIntl();
  const classes: CSSClassesList = processAndStageStylesV2();
  const userUOM = UserProfilePrefs.getInstance().getUserUnitPrefs();
  const fc = useFormContext<DairyV2Input>();

  return (
    <DsmGrid className={classes.dsmGridTwoColumnNoRowSpacing}>
      {!isStockEdit ? (
        <>
          {isFieldFilled(fc.getValues(`${fieldItemPrefix}.animalsPresentAtStart`)) ? (
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.animalsPresentAtStart`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_START' })}
                tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_START.TOOLTIP' })}
                type="number"
                metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_START.ADORNMENT' })}
                baseline={defaultStageData.animalsPresentAtStart}
                displayTypeSelect={false}
                disabled
              />
              <Spacer />
            </div>
          ) : (
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.originalAnimalsPresentAtStart`}
              label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_START' })}
              tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_START.TOOLTIP' })}
              adornment={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_START.ADORNMENT' })}
              type="number"
              defaultValue={defaultStageData.animalsPresentAtStart ?? ''}
              disabled
            />
          )}
          {isFieldFilled(fc.getValues(`${fieldItemPrefix}.animalsPresentAtEnd`)) ? (
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.animalsPresentAtEnd`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_END' })}
                tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_END.TOOLTIP' })}
                type="number"
                metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_END.ADORNMENT' })}
                baseline={defaultStageData.animalsPresentAtEnd}
                displayTypeSelect={false}
                disabled
              />
              <Spacer />
            </div>
          ) : (
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.originalAnimalsPresentAtEnd`}
              label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_END' })}
              tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_END.TOOLTIP' })}
              adornment={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_END.ADORNMENT' })}
              type="number"
              defaultValue={defaultStageData.animalsPresentAtEnd ?? ''}
              disabled
            />
          )}
          {isFieldFilled(fc.getValues(`${fieldItemPrefix}.averageWeightAtStart`)) ? (
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.averageWeightAtStart`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_START' })}
                tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_START.TOOLTIP' })}
                type="number"
                metricUnit={intl.formatMessage(
                  { id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_START.ADORNMENT' },
                  { unit: userUOM?.unitBarnOutputMass ? unitLong(userUOM.unitBarnOutputMass) : 'kg' }
                )}
                baseline={defaultStageData.averageWeightAtStart}
                displayTypeSelect={false}
                disabled
              />
              <Spacer />
            </div>
          ) : (
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.originalAverageWeightAtStart`}
              label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_START' })}
              tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_START.TOOLTIP' })}
              adornment={intl.formatMessage(
                { id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_START.ADORNMENT' },
                { unit: userUOM?.unitBarnOutputMass ? unitLong(userUOM.unitBarnOutputMass) : 'kg' }
              )}
              type="number"
              defaultValue={defaultStageData.averageWeightAtStart ?? ''}
              disabled
            />
          )}
          <div />
        </>
      ) : (
        <>
          <div>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix}.animalsPresentAtStart`}
              label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_START' })}
              tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_START.TOOLTIP' })}
              type="number"
              metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_START.ADORNMENT' })}
              baseline={defaultStageData.animalsPresentAtStart}
              disabled={false}
            />
            <Spacer />
          </div>
          <div>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix}.animalsPresentAtEnd`}
              label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_END' })}
              tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_END.TOOLTIP' })}
              type="number"
              metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_PRESENT_END.ADORNMENT' })}
              baseline={defaultStageData.animalsPresentAtEnd}
              disabled={false}
            />
            <Spacer />
          </div>
          <div>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix}.averageWeightAtStart`}
              label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_START' })}
              tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_START.TOOLTIP' })}
              type="number"
              metricUnit={intl.formatMessage(
                { id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_START.ADORNMENT' },
                { unit: userUOM?.unitBarnOutputMass ? unitLong(userUOM.unitBarnOutputMass) : 'kg' }
              )}
              baseline={defaultStageData.averageWeightAtStart}
              disabled={false}
            />
            <Spacer />
          </div>
          <div />
        </>
      )}
      {formType !== FormType.View &&
        <EditModeButtons
          isEditing={isStockEdit}
          onEdit={() => setIsStockEdit(true)}
          onCancel={resetEdit}
        />}
    </DsmGrid>
  )
};

export default DairyV2InputStockCowsComponent;
